import React, { useEffect, useRef, useState, useCallback, MutableRefObject } from 'react';
import { PixelStreamingHandler, ConnectionIssue } from '../sdk/PixelStreamingHandler';
import './BannerComponent.css';
import { UILayerComponent, PlaceholderView } from './UILayerComponent';
import { PixelStreamingCommunicator } from '../sdk/PixelStreamingCommunicator';
import { ConnectionIssueOverlay } from './ConnectionIssueOverlay';
import * as Sentry from '@sentry/react';

// Define a public API type for the banner
export interface BannerRef {
  startExperience: () => Promise<void>;
}

interface BannerProps {
  shareId: string;
  title?: string;
  description?: string;
  buttonText?: string;
  className?: string;
  triggerRef?: MutableRefObject<BannerRef | null>;
  initialSettings?: {
    StartVideoMuted?: boolean;
    AutoConnect?: boolean;
    AutoPlayVideo?: boolean;
    noSession?: boolean;
    [key: string]: any;
  };
  uiSettings?: {
    infoButton?: boolean;
    micButton?: boolean;
    audioButton?: boolean;
    fullscreenButton?: boolean;
    settingsButton?: boolean;
    connectionStrengthIcon?: boolean;
    [key: string]: any;
  };
}

interface OverlayProps {
  title?: string;
  description?: string;
  buttonText: string;
  onStart: () => void;
}

const BannerOverlay: React.FC<OverlayProps> = ({ title, description, buttonText, onStart }) => {
  return (
    <div className="plaza-banner__overlay-content">
      {title && <h1 className="plaza-banner__title">{title}</h1>}
      {description && <h3 className="plaza-banner__subtitle">{description}</h3>}
      <button 
        className="plaza-banner__button"
        onClick={onStart}
      >
        {buttonText}
      </button>
    </div>
  );
};

// Add new LoadingOverlay component
const LoadingOverlay: React.FC<{ message: string }> = ({ message }) => {
  const [progress, setProgress] = useState(0);
  const [showThrobber, setShowThrobber] = useState(false);

  useEffect(() => {
    const duration = 20000; // 20 seconds
    const interval = 50; // Update every 50ms
    const steps = duration / interval;
    const increment = 100 / steps;

    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          // Switch to throbber when progress reaches 100%
          setShowThrobber(true);
          return 100;
        }
        return prev + increment;
      });
    }, interval);

    return () => clearInterval(timer);
  }, []);

  if (showThrobber) {
    return (
      <div className="plaza-banner__overlay-content loading-state">
        <div className="throbber"></div>
        <h3 className="plaza-banner__subtitle">Connecting...</h3>
        <p className="loading-message">
          Connection is taking longer than expected. Please check your internet connection or wait a moment.
        </p>
      </div>
    );
  }

  return (
    <div className="plaza-banner__overlay-content loading-state">
      <h3 className="plaza-banner__subtitle">Your digital tour is starting</h3>
      <div className="progress-bar-container">
        <div 
          className="progress-bar" 
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="loading-message">{message}</p>
    </div>
  );
};

// Placeholder Image URLs
// const PLACEHOLDER_IMAGES = {
//   exterior: "https://schroeder-projekte.de/wp-content/uploads/2024/02/02-Titel-Muehlenbauerei-1920x1080px.jpg",
//   'interior-main': "https://images.unsplash.com/photo-1600210492493-0946911123ea?q=80&w=3348&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
//   'interior-stairwell': "https://wenning-natursteine.de/wp-content/uploads/wenning_20211102_DSC08034.jpg",
//   'interior-outside': "https://image.schoener-wohnen.de/12567088/t/nt/v18/w1440/r0/-/22755-sw-schwoerer-670-30-014-jpg--40493-.jpg",
// };

// Check for development mode flag
// console.log('[BannerComponent] Checking Environment Variables:', import.meta.env); // Keep commented out for potential future debugging
// console.log('[BannerComponent] VITE_DEV_MODE_NO_STREAMING value:', import.meta.env.VITE_DEV_MODE_NO_STREAMING); // Keep commented out for potential future debugging
const isDevModeNoStreaming = import.meta.env.VITE_DEV_MODE_NO_STREAMING === 'true';
if (isDevModeNoStreaming) {
  console.warn('DEV MODE: Pixel Streaming connection is disabled.');
} else {
  // console.log('DEV MODE: Pixel Streaming connection is ENABLED (VITE_DEV_MODE_NO_STREAMING is not \'true\').'); // Keep commented out for potential future debugging
}

// Global reference to prevent multiple instantiations and cleanup during remounts
let globalHandlerInstance: PixelStreamingHandler | null = null;
let globalCommunicatorInstance: PixelStreamingCommunicator | null = null;
// let handlerInitialized = false; // We'll manage initialization state differently
let isCleaningUp = false;

export const BannerComponent: React.FC<BannerProps> = ({
  shareId,
  title,
  description,
  buttonText = 'Start Experience',
  className = '',
  triggerRef,
  initialSettings = {},
  uiSettings = {}
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [handler, setHandler] = useState<PixelStreamingHandler | null>(null);
  const [communicator, setCommunicator] = useState<PixelStreamingCommunicator | null>(null);
  const [currentPlaceholderView, setCurrentPlaceholderView] = useState<PlaceholderView>('exterior');
  
  const instanceIdRef = useRef<string>(`banner-${Date.now()}-${Math.floor(Math.random() * 10000)}`);
  
  type OverlayState = 'initial' | 'loading' | 'queue' | 'streaming' | 'error';
  const [overlayState, setOverlayState] = useState<OverlayState>('initial');
  const [overlayMessage, setOverlayMessage] = useState<string>('');
  const [isStreamInteractive, setIsStreamInteractive] = useState<boolean>(false);
  const [connectionIssue, setConnectionIssue] = useState<ConnectionIssue | null>(null);
  const [queueMessage, setQueueMessage] = useState<string>('');
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentLoadingAttemptIdRef = useRef<string | null>(null); // Ref to track the current loading attempt
  const isInQueueRef = useRef<boolean>(false); // Track queue status to prevent timeout

  const handlePlaceholderViewChange = useCallback((view: PlaceholderView) => {
    setCurrentPlaceholderView(view);
  }, []);

  // Handle connection issues from PixelStreamingHandler
  const handleConnectionIssue = useCallback((issue: ConnectionIssue) => {
    console.log(`BannerComponent [${instanceIdRef.current}]: Connection issue received:`, issue);
    
    // Capture connection issue in Sentry
    Sentry.captureMessage('PixelStreaming connection issue', {
      level: issue.type === 'afk_timeout' ? 'warning' : 'error',
      extra: {
        issueType: issue.type,
        code: issue.code,
        reason: issue.reason,
        message: issue.message,
        shareId,
        instanceId: instanceIdRef.current,
        overlayState
      },
      tags: {
        component: 'banner_component',
        operation: 'connection_issue'
      }
    });
    
    // Handle connection issues with the overlay
    setConnectionIssue(issue);

    // For AFK timeout or critical connection issues, also set error state
    if (issue.type === 'afk_timeout' || issue.type === 'websocket_closed') {
      setOverlayState('error');
      setOverlayMessage(issue.message || 'Connection issue occurred');
    }
    // For AFK warnings, don't change the overlay state - just show the warning overlay
    // This allows users to continue interacting while being warned
  }, [shareId, instanceIdRef, overlayState]);

  // Handle queue status changes
  const handleQueueStatusChange = useCallback((inQueue: boolean, message?: string) => {
    console.log(`BannerComponent [${instanceIdRef.current}]: Queue status changed. InQueue: ${inQueue}, Message: ${message}`);
    
    isInQueueRef.current = inQueue;
    
    if (inQueue) {
      setOverlayState('queue');
      setQueueMessage(message || 'You are in the queue. Please wait...');
      
      // Clear the loading timeout since we're in queue
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
        console.log(`BannerComponent [${instanceIdRef.current}]: Loading timeout cleared due to queue entry`);
      }
    } else {
      // Exiting queue - return to loading state if we were in queue
      if (overlayState === 'queue') {
        setOverlayState('loading');
        setOverlayMessage('Connecting...');
      }
    }
  }, [overlayState]);

  // Dismiss connection issue overlay
  const handleDismissIssue = useCallback(() => {
    console.log(`BannerComponent [${instanceIdRef.current}]: Dismissing connection issue`);
    setConnectionIssue(null);
  }, []);

  // Auto-dismiss AFK warnings when user interacts with the page
  const handleUserActivity = useCallback(() => {
    if (connectionIssue && (connectionIssue.type === 'afk_warning' || connectionIssue.type === 'afk_warning_update')) {
      console.log(`BannerComponent [${instanceIdRef.current}]: User activity detected, dismissing AFK warning`);
      setConnectionIssue(null);
    }
  }, [connectionIssue]);

  // Function to find and hide the triggering img element
  const hideTriggerElement = useCallback(() => {
    try {
      // Look for the banner's container element in the DOM
      const bannerContainer = containerRef.current;
      if (!bannerContainer) return;

      // Find the closest parent with Elementor identifiers
      let currentElement = bannerContainer.parentElement;
      while (currentElement && currentElement !== document.body) {
        // Check for Elementor widget identifiers
        if (currentElement.hasAttribute('data-id') && 
            currentElement.hasAttribute('data-element_type')) {
          
          // Find the img element within this Elementor widget
          const imgElement = currentElement.querySelector('img');
          if (imgElement) {
            console.log(`BannerComponent [${instanceIdRef.current}]: Found img element in Elementor widget, hiding it`);
            imgElement.style.display = 'none';
          } else {
            console.log(`BannerComponent [${instanceIdRef.current}]: No img element found in Elementor widget`);
          }
          return;
        }
        
        // Also check for other common CMS/builder identifiers
        if (currentElement.classList.contains('elementor-element')) {
          // Find the img element within this Elementor element
          const imgElement = currentElement.querySelector('img');
          if (imgElement) {
            console.log(`BannerComponent [${instanceIdRef.current}]: Found img element in Elementor element, hiding it`);
            imgElement.style.display = 'none';
          } else {
            console.log(`BannerComponent [${instanceIdRef.current}]: No img element found in Elementor element`);
          }
          return;
        }
        
        currentElement = currentElement.parentElement;
      }
      
      console.log(`BannerComponent [${instanceIdRef.current}]: No trigger element found to hide`);
    } catch (error) {
      console.warn(`BannerComponent [${instanceIdRef.current}]: Error hiding trigger element:`, error);
    }
  }, []);

  const startStreamingExperience = useCallback(async () => {
    return Sentry.startSpan({
      op: 'user.interaction',
      name: 'Start Streaming Experience'
    }, async (span: any) => {
      span.setAttribute('shareId', shareId);
      span.setAttribute('overlayState', overlayState);
      span.setAttribute('hasGlobalHandler', !!globalHandlerInstance);
      span.setAttribute('instanceId', instanceIdRef.current);
      
      console.log(`BannerComponent [${instanceIdRef.current}]: startStreamingExperience called. Current overlayState: ${overlayState}, Global Handler Exists: ${!!globalHandlerInstance}`);
      
      // Hide the triggering element
      hideTriggerElement();
      
      if (overlayState === 'loading' || overlayState === 'streaming') {
        console.log(`BannerComponent [${instanceIdRef.current}]: Action ignored. Already loading or streaming. State: ${overlayState}`);
        return;
      }

    if (isDevModeNoStreaming) {
      console.log(`BannerComponent [${instanceIdRef.current}]: Dev mode, no streaming. Setting overlay to streaming.`);
      setOverlayState('streaming');
      setIsStreamInteractive(true);
      return;
    }

    console.log(`BannerComponent [${instanceIdRef.current}]: Setting overlayState to 'loading'.`);
    setOverlayState('loading');
    setOverlayMessage('Connecting...');
    setConnectionIssue(null); // Clear any previous connection issues

    const attemptId = `load-${Date.now()}`;
    currentLoadingAttemptIdRef.current = attemptId;
    console.log(`BannerComponent [${instanceIdRef.current}]: Setting currentLoadingAttemptIdRef to: ${attemptId}`);

    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      console.warn(`BannerComponent [${instanceIdRef.current}]: Loading timeout reached (100s). Checking attemptId: ${attemptId}, currentLoadingAttemptIdRef: ${currentLoadingAttemptIdRef.current}, isInQueue: ${isInQueueRef.current}`);
      
      // Don't timeout if we're in queue - the queue handler will manage the user experience
      if (isInQueueRef.current) {
        console.log(`BannerComponent [${instanceIdRef.current}]: In queue, skipping timeout. Queue will manage the connection.`);
        return;
      }
      
      // Only act if this specific loading attempt is still the active one and hasn't been cleared
      if (currentLoadingAttemptIdRef.current === attemptId) { 
        console.error(`BannerComponent [${instanceIdRef.current}]: Timeout for loading attempt ${attemptId}. Setting error state.`);
        
        // Capture timeout as Sentry event
        Sentry.captureMessage('Streaming experience loading timeout', {
          level: 'warning',
          extra: {
            attemptId,
            shareId,
            overlayState,
            isInQueue: isInQueueRef.current,
            instanceId: instanceIdRef.current,
            hasGlobalHandler: !!globalHandlerInstance
          },
          tags: {
            component: 'banner_component',
            operation: 'loading_timeout'
          }
        });
        
        currentLoadingAttemptIdRef.current = null; // Clear the active attempt ID
        setOverlayState('error');
        setOverlayMessage('Connection could not be established (timeout). Please try again later.');
        if (globalHandlerInstance) {
            console.log(`BannerComponent [${instanceIdRef.current}]: Timeout: Disconnecting globalHandlerInstance.`);
            globalHandlerInstance.disconnect(); 
        }
        globalHandlerInstance = null; globalCommunicatorInstance = null; setHandler(null); setCommunicator(null);
      }
    }, 100000);

    try {
      if (globalHandlerInstance) {
        console.log(`BannerComponent [${instanceIdRef.current}]: Existing globalHandlerInstance found. Querying its state...`);
        const activeInstances = globalHandlerInstance.getActiveInstances();
        const wsStates: (0 | 1 | 2 | 3)[] | null = globalHandlerInstance.getWebsocketStates() as (0 | 1 | 2 | 3)[] | null;
        console.log(`BannerComponent [${instanceIdRef.current}]: SDK State - ActiveInstances: ${activeInstances}, WebsocketStates: ${JSON.stringify(wsStates)}`);

        let trulyActiveSession = false;
        if (activeInstances !== null && activeInstances > 0 && wsStates && wsStates.length > 0) {
          trulyActiveSession = wsStates.some(state => state === 0 /* CONNECTING */ || state === 1 /* OPEN */);
          if (trulyActiveSession) {
            console.log(`BannerComponent [${instanceIdRef.current}]: WebSocket state is CONNECTING (0) or OPEN (1). Assuming active session.`);
          } else {
            console.log(`BannerComponent [${instanceIdRef.current}]: ActiveInstances > 0 but no WebSocket in CONNECTING or OPEN state. States: ${JSON.stringify(wsStates)}. Will attempt to connect.`);
          }
        } else {
          console.log(`BannerComponent [${instanceIdRef.current}]: No active instances reported, or WebSocket states are not conducive to re-engagement (Active: ${activeInstances}, States: ${JSON.stringify(wsStates)}).`);
        }

        if (trulyActiveSession) {
          console.log(`BannerComponent [${instanceIdRef.current}]: Re-engaging with existing active stream (trulyActiveSession = true).`);
          if (globalCommunicatorInstance) setCommunicator(globalCommunicatorInstance);
          setHandler(globalHandlerInstance);
          console.log(`BannerComponent [${instanceIdRef.current}]: Overlay set to loading, awaiting EndLoadingScreen from active stream.`);
        } else {
          console.log(`BannerComponent [${instanceIdRef.current}]: No truly active session to re-engage (trulyActiveSession = false). Attempting to connect existing handler.`);
          if (globalCommunicatorInstance) setCommunicator(globalCommunicatorInstance);
          setHandler(globalHandlerInstance);
          console.log(`BannerComponent [${instanceIdRef.current}]: Calling connect() on existing globalHandlerInstance.`);
          await globalHandlerInstance.connect();
          console.log(`BannerComponent [${instanceIdRef.current}]: globalHandlerInstance.connect() completed.`);
        }
      } else {
        console.log(`BannerComponent [${instanceIdRef.current}]: No existing globalHandlerInstance. Creating a new one.`);
        if (!containerRef.current) {
            console.error('BannerComponent: Container ref not available for new instance.');
            throw new Error('Container ref not available for new instance.');
        }
        const videoContainer = containerRef.current.querySelector<HTMLDivElement>('.plaza-banner__video-container');
        if (!videoContainer) {
            console.error('BannerComponent: Video container not found for new instance.');
            throw new Error('Video container not found for new instance.');
        }

        console.log(`BannerComponent [${instanceIdRef.current}]: Instantiating new PixelStreamingHandler.`);
        const psHandler = new PixelStreamingHandler({
          shareId,
          containerElement: videoContainer,
          initialSettings: { ...initialSettings, AutoConnect: false, AutoPlayVideo: false, noSession: true },
          uiSettings,
          onConnect: () => {
            console.log(`BannerComponent [${instanceIdRef.current}]: <<< PixelStreaming connected (onConnect callback from handler) >>>`);
            if (globalCommunicatorInstance) {
              console.log(`BannerComponent [${instanceIdRef.current}]: onConnect: Setting communicator streamIsReady to true.`);
              globalCommunicatorInstance.setStreamIsReady(true);
              console.log(`BannerComponent [${instanceIdRef.current}]: onConnect: Starting communicator listeners and health check.`);
              globalCommunicatorInstance.startListeningToResponses();
              globalCommunicatorInstance.startHealthCheck();
            }
          },
          onDisconnect: () => {
            if (isCleaningUp) return;
            console.warn(`BannerComponent [${instanceIdRef.current}]: <<< PixelStreaming disconnected (onDisconnect callback from handler) >>> Current overlayState: ${overlayState}`);
            setIsStreamInteractive(false);
            if (globalCommunicatorInstance) globalCommunicatorInstance.setStreamIsReady(false);
            if (overlayState !== 'initial') {
              console.error(`BannerComponent [${instanceIdRef.current}]: onDisconnect: Setting error state due to disconnect.`);
              setOverlayState('error');
              setOverlayMessage('Connection lost. Please reload the page or try again later.');
            }
          },
          onError: (error) => {
            if (isCleaningUp) return;
            console.error(`BannerComponent [${instanceIdRef.current}]: <<< PixelStreaming Handler error (onError callback from handler) >>>`, error);
            setOverlayState('error');
            setOverlayMessage(`Connection error: ${error.message || 'Unknown error'}.`);
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
          },
          onConnectionIssue: handleConnectionIssue,
          onQueueStatusChange: handleQueueStatusChange
        });
        globalHandlerInstance = psHandler;
        setHandler(psHandler);
        console.log(`BannerComponent [${instanceIdRef.current}]: New PixelStreamingHandler instantiated. Creating new PixelStreamingCommunicator.`);
        const newCommunicator = new PixelStreamingCommunicator(psHandler);
        globalCommunicatorInstance = newCommunicator;
        setCommunicator(newCommunicator);

        console.log(`BannerComponent [${instanceIdRef.current}]: Initializing new PixelStreamingHandler (psHandler.initialize())...`);
        await psHandler.initialize();
        console.log(`BannerComponent [${instanceIdRef.current}]: New PixelStreamingHandler initialized. Connecting (psHandler.connect())...`);
        await psHandler.connect();
        console.log(`BannerComponent [${instanceIdRef.current}]: New psHandler.connect() called.`);
      }
      } catch (error) {
        const typedError = error instanceof Error ? error : new Error(String(error));
        console.error(`BannerComponent [${instanceIdRef.current}]: Error during startStreamingExperience:`, typedError.message, typedError.stack);
        
        // Capture error in Sentry
        Sentry.captureException(typedError, {
          extra: {
            shareId,
            overlayState,
            instanceId: instanceIdRef.current,
            attemptId,
            hasGlobalHandler: !!globalHandlerInstance,
            initialSettings,
            uiSettings
          },
          tags: {
            component: 'banner_component',
            operation: 'start_streaming_experience'
          }
        });
        
        setOverlayState('error');
        setOverlayMessage(typedError.message || 'Unknown error starting the streaming service.');
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      }
    });
  }, [shareId, initialSettings, uiSettings, instanceIdRef, overlayState, handleConnectionIssue, hideTriggerElement]);

  // Retry connection after issue
  const handleRetryConnection = useCallback(() => {
    Sentry.startSpan({
      op: 'user.interaction',
      name: 'Retry Connection'
    }, (span: any) => {
      span.setAttribute('shareId', shareId);
      span.setAttribute('instanceId', instanceIdRef.current);
      span.setAttribute('connectionIssueType', connectionIssue?.type || 'unknown');
      
      console.log(`BannerComponent [${instanceIdRef.current}]: Retrying connection after issue`);
      setConnectionIssue(null);
      
      // Reset states and restart the streaming experience
      if (globalHandlerInstance) {
        globalHandlerInstance.disconnect();
      }
      globalHandlerInstance = null;
      globalCommunicatorInstance = null;
      setHandler(null);
      setCommunicator(null);
      
      // Restart the experience
      setTimeout(() => {
        startStreamingExperience();
      }, 500);
    });
  }, [shareId, connectionIssue, instanceIdRef, startStreamingExperience]);

  useEffect(() => {
    if (!communicator || isDevModeNoStreaming) return;
    
    console.log(`BannerComponent [${instanceIdRef.current}]: Setting up EndLoadingScreen listener with communicator.`);
    const listenerId = communicator.addEndLoadingScreenListener(() => {
      console.log(`BannerComponent [${instanceIdRef.current}]: <<< EndLoadingScreen event received from UE >>>.`);
      
      // Clear the specific loading attempt that was in progress
      if (currentLoadingAttemptIdRef.current) {
        console.log(`BannerComponent [${instanceIdRef.current}]: EndLoadingScreen: Clearing currentLoadingAttemptIdRef: ${currentLoadingAttemptIdRef.current}`);
        currentLoadingAttemptIdRef.current = null;
      }

      if (loadingTimeoutRef.current) { 
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
        console.log(`BannerComponent [${instanceIdRef.current}]: Loading timeout cleared due to EndLoadingScreen.`);
      }
      console.log(`BannerComponent [${instanceIdRef.current}]: EndLoadingScreen: Setting overlayState to 'streaming', isStreamInteractive to true.`);
      setOverlayState('streaming'); 
      setIsStreamInteractive(true); 
      if (communicator) {
        console.log(`BannerComponent [${instanceIdRef.current}]: EndLoadingScreen: Setting communicator streamIsReady to true.`);
        communicator.setStreamIsReady(true);
      }
    });
    return () => {
      console.log(`BannerComponent [${instanceIdRef.current}]: Cleaning up EndLoadingScreen listener ID: ${listenerId}`);
      if (communicator) {
        communicator.removeEndLoadingScreenListener(listenerId);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
         console.log(`BannerComponent [${instanceIdRef.current}]: Loading timeout cleared during EndLoadingScreen cleanup (unmount).`);
        currentLoadingAttemptIdRef.current = null; // Also clear attempt ID on unmount if timeout was active
      }
    };
  }, [communicator, instanceIdRef, isDevModeNoStreaming]);

  useEffect(() => {
    if (triggerRef) {
      triggerRef.current = {
        startExperience: startStreamingExperience
      };
    }
  }, [triggerRef, startStreamingExperience]);

  // Add user activity listeners to auto-dismiss AFK warnings
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [handleUserActivity]);

  useEffect(() => {
    const currentInstanceId = instanceIdRef.current;
    console.log(`BannerComponent [${currentInstanceId}]: Mount/Cleanup effect for instance ${currentInstanceId}.`);
    isCleaningUp = false;
    return () => {
      isCleaningUp = true;
      console.log(`BannerComponent [${currentInstanceId}]: Starting cleanup for instance ${currentInstanceId}.`);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        console.log(`BannerComponent [${currentInstanceId}]: Loading timeout cleared during main cleanup.`);
        currentLoadingAttemptIdRef.current = null; // Also clear attempt ID on main cleanup
      }
      if (globalHandlerInstance) {
        console.log(`BannerComponent [${currentInstanceId}]: Disconnecting global PixelStreaming handler (instance ${currentInstanceId}).`);
        globalHandlerInstance.disconnect();
        globalHandlerInstance = null; 
      }
      if (globalCommunicatorInstance) {
        console.log(`BannerComponent [${currentInstanceId}]: Cleaning up global PixelStreaming communicator (instance ${currentInstanceId}).`);
        globalCommunicatorInstance.cleanup(); 
        globalCommunicatorInstance = null; 
      }
      setHandler(null);
      setCommunicator(null);
      setIsStreamInteractive(false);
      console.log(`BannerComponent [${currentInstanceId}]: Cleanup complete for instance ${currentInstanceId}.`);
    };
  }, [instanceIdRef]); 

  // Intersection Observer for video performance optimization
  useEffect(() => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(error => {
            console.warn('Video autoplay failed:', error);
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    
    observer.observe(video);
    
    return () => {
      observer.disconnect();
    };
  }, [overlayState]);

  const getOverlayContent = () => {
    switch (overlayState) {
      case 'initial':
        return <BannerOverlay title={title} description={description} buttonText={buttonText} onStart={startStreamingExperience} />;
      case 'loading':
        return <LoadingOverlay message={overlayMessage || 'Connecting...'} />;
      case 'queue':
        return (
          <div className="plaza-banner__overlay-content queue-state">
            <div className="throbber"></div>
            <h3 className="plaza-banner__subtitle">In Queue</h3>
            <p className="queue-message">
              {queueMessage || 'You are in the queue. Please wait until a stream becomes available.'}
            </p>
            <p className="queue-info">
              Connection will be established automatically when capacity is available. Please keep this window open.
            </p>
          </div>
        );
      case 'error':
        return (
          <div className="plaza-banner__overlay-content error-state">
            <h2>Error</h2>
            <p>{overlayMessage || 'An unknown error occurred.'}</p>
            <button onClick={startStreamingExperience} className="plaza-banner__button retry-button">
              Try Again
            </button>
          </div>
        );
      case 'streaming':
        return null; 
      default:
        return null;
    }
  };

  const showOverlay = overlayState === 'initial' || overlayState === 'loading' || overlayState === 'queue' || overlayState === 'error';

  return (
    <div ref={containerRef} className={`plaza-banner ${className} plaza-banner--placeholder-${currentPlaceholderView} ${showOverlay ? 'overlay-active' : ''}`}>
      <div className="plaza-banner__video-container"></div>
      {showOverlay && (
        <>
          <video 
            ref={videoRef}
            className="plaza-banner__bg-video"
            autoPlay 
            muted 
            loop 
            playsInline
            preload="auto"
          >
            <source src="https://d13w4oqr9lpyzh.cloudfront.net/plaza-bg.av1.webm" type='video/webm; codecs="av01.0.05M.08,opus"' />
            <source src="https://d13w4oqr9lpyzh.cloudfront.net/plaza-bg.mp4" type="video/mp4" />
          </video>
          <div className="plaza-banner__overlay">
            {getOverlayContent()}
          </div>
        </>
       )}
      {connectionIssue && (
        <ConnectionIssueOverlay 
          issue={connectionIssue}
          onRetry={handleRetryConnection}
          onDismiss={handleDismissIssue}
        />
      )}
      {(overlayState === 'streaming' || (isDevModeNoStreaming && isStreamInteractive)) && handler && communicator && (
        <UILayerComponent 
          handler={handler} 
          onPlaceholderViewChange={handlePlaceholderViewChange} 
          isStreamInteractive={isStreamInteractive}
          communicator={communicator}
        />
      )}
    </div>
  );
};